/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import adminApi from '../services/adminApi';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiryWarning, setSessionExpiryWarning] = useState(false);

  /**
   * Initialize - Check if user is already logged in
   */
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('adminAccessToken');
      const savedUser = localStorage.getItem('adminUser');

      if (token && savedUser) {
        try {
          // Verify session with backend
          const response = await adminApi.verifySession();
          setUser(response.data.user);
          setSession(response.data.session);
        } catch (error) {
          // Session invalid, clear storage
          logout();
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  /**
   * Session expiry check
   */
  useEffect(() => {
    if (!session) return;

    const checkExpiry = () => {
      const minutesRemaining = session.minutesUntilExpiry;

      // Show warning if less than 10 minutes remaining
      if (minutesRemaining < 10 && minutesRemaining > 0) {
        setSessionExpiryWarning(true);
      } else {
        setSessionExpiryWarning(false);
      }

      // Auto logout if expired
      if (minutesRemaining <= 0) {
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkExpiry, 60000);
    checkExpiry(); // Check immediately

    return () => clearInterval(interval);
  }, [session]);

  /**
   * Login - Step 1: Password verification
   * Returns requires2FA: true if 2FA is enabled
   */
  const login = async (username, password, captchaToken) => {
    try {
      const response = await adminApi.login(username, password, captchaToken);

      // Check if 2FA is required
      if (response.requires2FA) {
        return {
          success: false,
          requires2FA: true,
          data: response.data
        };
      }

      // No 2FA - complete login
      localStorage.setItem('adminAccessToken', response.data.tokens.accessToken);
      localStorage.setItem('adminRefreshToken', response.data.tokens.refreshToken);
      localStorage.setItem('adminUser', JSON.stringify(response.data.user));
      
      // Store session ID for API calls
      if (response.data.session?.sessionId) {
        localStorage.setItem('adminSessionId', response.data.session.sessionId);
      }

      // Store 2FA session token if provided (for non-2FA users who later enable it)
      if (response.data.twoFASession) {
        localStorage.setItem('admin2FASession', response.data.twoFASession);
      }

      setUser(response.data.user);
      setSession(response.data.session);

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  /**
   * Verify 2FA - Step 2: Complete login with 2FA code
   * @param {string} tempToken - Temporary token from login step
   * @param {string} code - 6-digit TOTP code or backup code
   * @param {boolean} isBackupCode - Whether using a backup code
   */
  const verify2FA = async (tempToken, code, isBackupCode = false) => {
    try {
      const response = await adminApi.verify2FA(tempToken, code, isBackupCode);

      if (response.success) {
        // Store tokens
        localStorage.setItem('adminAccessToken', response.data.tokens.accessToken);
        localStorage.setItem('adminRefreshToken', response.data.tokens.refreshToken);
        localStorage.setItem('adminUser', JSON.stringify(response.data.user));
        
        // Store session ID for API calls
        if (response.data.session?.sessionId) {
          localStorage.setItem('adminSessionId', response.data.session.sessionId);
        }

        // ============================================================
        // CRITICAL FIX: Store 2FA session token for subsequent API calls
        // ============================================================
        if (response.data.twoFASession) {
          localStorage.setItem('admin2FASession', response.data.twoFASession);
          console.log('[AdminAuth] 2FA session token stored');
        }

        // Update state
        setUser(response.data.user);
        setSession(response.data.session);

        return { success: true, data: response.data };
      }

      return { success: false, error: response.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || '2FA verification failed'
      };
    }
  };

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout error:', error);
    }

    // Clear local state - including 2FA session
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminSessionId');
    localStorage.removeItem('admin2FASession');  // ADDED: Clear 2FA session on logout
    setUser(null);
    setSession(null);
    setSessionExpiryWarning(false);
  }, []);

  /**
   * Change password
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await adminApi.changePassword(currentPassword, newPassword);
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Password change failed'
      };
    }
  };

  /**
   * Refresh session info
   */
  const refreshSession = async () => {
    try {
      const response = await adminApi.verifySession();
      setUser(response.data.user);
      setSession(response.data.session);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Check if user has permission
   */
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  /**
   * Check if user has any of the permissions
   */
  const hasAnyPermission = (permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.some(p => user.permissions.includes(p));
  };

  /**
   * Check if user has role
   */
  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  // ========== 2FA MANAGEMENT METHODS ==========

  /**
   * Get 2FA status for current user
   */
  const get2FAStatus = async () => {
    try {
      const response = await adminApi.get2FAStatus();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get 2FA status'
      };
    }
  };

  /**
   * Initiate 2FA setup - get QR code
   */
  const setup2FA = async () => {
    try {
      const response = await adminApi.setup2FA();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || '2FA setup failed'
      };
    }
  };

  /**
   * Enable 2FA with verification code
   */
  const enable2FA = async (code) => {
    try {
      const response = await adminApi.enable2FA(code);
      
      // Update user state to reflect 2FA enabled
      if (response.success && user) {
        const updatedUser = { ...user, twoFactorEnabled: true };
        setUser(updatedUser);
        localStorage.setItem('adminUser', JSON.stringify(updatedUser));
      }
      
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to enable 2FA'
      };
    }
  };

  /**
   * Disable 2FA
   */
  const disable2FA = async (password) => {
    try {
      const response = await adminApi.disable2FA(password);
      
      // Update user state to reflect 2FA disabled
      if (response.success && user) {
        const updatedUser = { ...user, twoFactorEnabled: false };
        setUser(updatedUser);
        localStorage.setItem('adminUser', JSON.stringify(updatedUser));
        // Clear 2FA session since 2FA is now disabled
        localStorage.removeItem('admin2FASession');
      }
      
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to disable 2FA'
      };
    }
  };

  /**
   * Regenerate backup codes
   */
  const regenerateBackupCodes = async (password) => {
    try {
      const response = await adminApi.regenerateBackupCodes(password);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to regenerate backup codes'
      };
    }
  };

  const value = {
    user,
    session,
    loading,
    sessionExpiryWarning,
    isAuthenticated: !!user,
    // Auth methods
    login,
    verify2FA,
    logout,
    changePassword,
    refreshSession,
    // Permission methods
    hasPermission,
    hasAnyPermission,
    hasRole,
    // 2FA management methods
    get2FAStatus,
    setup2FA,
    enable2FA,
    disable2FA,
    regenerateBackupCodes
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
